/**
 * Bridges Lev auth → Courier inbox auth.
 *
 * Sits inside the Clerk + tRPC provider tree. When the current user is
 * signed in, fetches a short-lived Courier JWT from `notification.inboxToken`,
 * signs the global `Courier` singleton in (once per token), registers the
 * default inbox feed, and starts the realtime listener. Mount once near the
 * top of the tree — `useCourier()` reads from a singleton so any descendant
 * (e.g. `NotificationBell`) can call `useCourier()` without a context wrapper.
 *
 * Token rotates ahead of its 12h expiry by refetching at 10h.
 *
 * Realtime resilience: `listenForUpdates()` (the WebSocket) is started once on
 * sign-in, but the socket dies on sleep / tab-background / network blips and
 * `courier-js` permanently gives up after its retry budget — leaving the bell
 * stale until a reload. `startInboxRealtimeKeepalive` re-establishes it on
 * tab-focus, network-online, and a periodic backstop. See LEV-1035.
 *
 * Every load/listen call MUST end in a `.catch` that reports through
 * `captureClientError`: an uncaught rejection (e.g. a dropped
 * `inbox.courier.com` request) reaches Sentry as an unhandled error and pages
 * #monitoring. `loadThenListen` and `listen` are the only call paths.
 * Only the first of consecutive failures is captured. The keepalive acts
 * only once Courier is signed in, and its backstop reloads (a few times at
 * most) until the first load succeeds, because a bare re-listen never
 * repeats a failed first fetch.
 */
"use client";

import { useCallback, useEffect, useRef } from "react";
import { useUser } from "@clerk/nextjs";
import { useCourier } from "@trycourier/courier-react";

import { api } from "@/lib/trpc/react";
import { startInboxRealtimeKeepalive } from "@/lib/courier/inboxRealtimeKeepalive";
import { captureClientError } from "@/lib/sentry/capture-client";

const TOKEN_REFETCH_MS = 10 * 60 * 60 * 1000; // 10h, ahead of 12h expiry.

/** Backstop reloads allowed after consecutive load failures; tab-focus and
 * `online` still retry past this, so an offline tab stops polling Courier. */
const MAX_BACKSTOP_RELOADS = 3;

function captureInboxError(error: unknown, errorType: "inbox-load" | "inbox-listen") {
  // "degraded", not "blocking": a dead bell is a missed notification, not a
  // broken app. captureClientError downgrades a network drop
  // (TypeError: Failed to fetch) to "transient" whatever impact is passed.
  captureClientError(error, { feature: "courier-inbox", errorType, impact: "degraded" });
}

/** Per-sign-in inbox health. Only the FIRST of a run of consecutive failures
 * is captured, so a tab retrying while offline reports once, not every tick. */
interface InboxLoadState {
  userId: string | null;
  loaded: boolean;
  /** A load is pending; later triggers skip instead of stacking requests. */
  loading: boolean;
  loadFailures: number;
  listenFailures: number;
}

function freshLoadState(userId: string | null): InboxLoadState {
  return { userId, loaded: false, loading: false, loadFailures: 0, listenFailures: 0 };
}

export function CourierInboxBridge() {
  const { isSignedIn } = useUser();
  const { auth, inbox } = useCourier();
  // Track the (userId, token) pair we last signed in with so a JWT rotation
  // for the same user re-runs signIn instead of no-op'ing.
  const signedInWithRef = useRef<{ userId: string; token: string } | null>(null);
  const loadStateRef = useRef<InboxLoadState>(freshLoadState(null));

  // Keep fresh refs to the inbox actions so the keepalive effect below doesn't
  // need `inbox` in its deps — `useCourier` returns a brand-new `inbox` object
  // on every datastore refresh, which would otherwise re-run the effect (and
  // re-wire its listeners) on every notification.
  const inboxLoadRef = useRef(inbox.load);
  const inboxListenRef = useRef(inbox.listenForUpdates);
  useEffect(() => {
    inboxLoadRef.current = inbox.load;
    inboxListenRef.current = inbox.listenForUpdates;
  });

  // Every listen call goes through here: rejections are caught, and only the
  // first of consecutive failures is captured.
  const listen = useCallback(() => {
    const state = loadStateRef.current;
    void inboxListenRef
      .current()
      .then(() => {
        if (loadStateRef.current === state) state.listenFailures = 0;
      })
      .catch((error) => {
        if (loadStateRef.current !== state) return;
        state.listenFailures += 1;
        if (state.listenFailures === 1) captureInboxError(error, "inbox-listen");
      });
  }, []);

  // Every load goes through here. One load runs at a time per sign-in, so a
  // stalled request can't stack up more. A result is applied only if the same
  // sign-in is still current, so a late reply for a previous user can't mark
  // the next user's feed as loaded.
  const loadThenListen = useCallback(() => {
    const state = loadStateRef.current;
    if (!state.userId || state.loading) return;
    state.loading = true;
    void inboxLoadRef
      .current()
      .finally(() => {
        state.loading = false;
      })
      .then(() => {
        if (loadStateRef.current !== state) return;
        state.loaded = true;
        state.loadFailures = 0;
        listen();
      })
      .catch((error) => {
        if (loadStateRef.current !== state) return;
        state.loadFailures += 1;
        if (state.loadFailures === 1) captureInboxError(error, "inbox-load");
      });
  }, [listen]);

  const { data } = api.notification.inboxToken.useQuery(undefined, {
    enabled: !!isSignedIn,
    refetchInterval: TOKEN_REFETCH_MS,
    refetchOnWindowFocus: false,
    staleTime: TOKEN_REFETCH_MS,
  });

  useEffect(() => {
    if (!isSignedIn) {
      if (signedInWithRef.current) {
        auth.signOut();
        signedInWithRef.current = null;
      }
      loadStateRef.current = freshLoadState(null);
      return;
    }
    if (!data?.token || !data.userId) return;
    const current = signedInWithRef.current;
    if (current && current.userId === data.userId && current.token === data.token) {
      return;
    }

    auth.signIn({ userId: data.userId, jwt: data.token });
    const isFreshSignIn = !current || current.userId !== data.userId;
    signedInWithRef.current = { userId: data.userId, token: data.token };
    // Feed registration + load only needs to happen on a fresh sign-in,
    // not on a token rotation for the same user.
    if (!isFreshSignIn) return;

    inbox.registerFeeds([
      {
        feedId: "inbox",
        title: "Inbox",
        tabs: [{ datasetId: "inbox", title: "All", filter: {} }],
      },
    ]);
    loadStateRef.current = freshLoadState(data.userId);
    loadThenListen();
  }, [isSignedIn, data?.token, data?.userId, auth, inbox, loadThenListen]);

  // Re-establish the realtime socket after it dies (sleep / tab-background /
  // network), since `courier-js` permanently gives up after its retry budget
  // and nothing else re-invokes `listenForUpdates()`. Without this the bell
  // goes stale until a full page reload. See module docs for the failure mode.
  useEffect(() => {
    if (!isSignedIn) return;
    return startInboxRealtimeKeepalive({
      // Returned-to-app: refetch so messages delivered while the socket was
      // dead show up, then re-attach the realtime listener.
      resync: loadThenListen,
      // Foreground backstop: re-listen once the feed has loaded (no-op when
      // connected); until then, reload, at most MAX_BACKSTOP_RELOADS times
      // in a row.
      ensureListening: () => {
        const state = loadStateRef.current;
        if (!state.userId) return;
        if (state.loaded) {
          listen();
          return;
        }
        if (state.loadFailures < MAX_BACKSTOP_RELOADS) loadThenListen();
      },
    });
  }, [isSignedIn, listen, loadThenListen]);

  return null;
}
